/* PACKAGES */
import React, { useEffect, useRef, useState } from 'react';

/* ELEMENTS */
import Button from '../Button/Button';

/* STYLES */
import s from './Upload.scss';

const Upload = ({ variantClasses = [], id, inputChange, error = false, placeholder = null, label = null, buttonLabel = 'Upload files', buttonInfoLabel = null, children, ...props }) => {
	const fileInputRef = useRef(null);
	const dragDepthRef = useRef(0);
	const [files, setFiles] = useState([]);
	const allowMultiple = !!(props && props.multiple);

	const toFileList = (fileArray) => {
		try {
			const dt = new DataTransfer();
			fileArray.forEach((f) => dt.items.add(f));
			return dt.files;
		} catch {
			// Fallback: return array; parent can handle either
			return fileArray;
		}
	};

	const syncToParentAndInput = (nextFiles) => {
		setFiles(nextFiles);
		const fileList = toFileList(nextFiles);
		// Notify parent
		inputChange(id, fileList);
		// Try to sync the actual <input> files (supported in modern browsers)
		if (fileInputRef.current && fileInputRef.current.type === 'file' && fileInputRef.current.files) {
			try {
				fileInputRef.current.files = fileList;
			} catch {
				// some browsers disallow assignment; ignore
			}
		}
	};

	const handleFiles = (incoming) => {
		if (!incoming) return;
		const incomingArr = Array.from(incoming);
		if (incomingArr.length === 0) return;
		const next = allowMultiple ? [...files, ...incomingArr] : [incomingArr[0]];
		syncToParentAndInput(next);
	};

	const inputChangeHandler = (e) => {
		e.preventDefault();
		const $input = e.currentTarget;

		handleFiles($input.files);
	};

	useEffect(() => {
		const onDragEnter = (e) => {
			e.preventDefault();
			dragDepthRef.current += 1;
			document.querySelector('html').classList.add('u_dropActive');
		};

		const onDragOver = (e) => {
			e.preventDefault();
			if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
		};

		const clearDropState = () => {
			dragDepthRef.current = 0;
			document.querySelector('html').classList.remove('u_dropActive');
		};

		const onDragLeave = (e) => {
			e.preventDefault();
			dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
			if (dragDepthRef.current === 0) clearDropState();
			document.querySelector('html').classList.remove('u_dropActive');
		};

		const onDrop = (e) => {
			e.preventDefault();
			const dt = e.dataTransfer;
			const dropped = dt?.files;
			clearDropState();
			if (dropped && dropped.length) handleFiles(dropped);
		};

		window.addEventListener('dragenter', onDragEnter);
		window.addEventListener('dragover', onDragOver);
		window.addEventListener('dragleave', onDragLeave);
		window.addEventListener('drop', onDrop);
		window.addEventListener('dragend', onDragLeave);

		return () => {
			window.removeEventListener('dragenter', onDragEnter);
			window.removeEventListener('dragover', onDragOver);
			window.removeEventListener('dragleave', onDragLeave);
			window.removeEventListener('drop', onDrop);
			window.removeEventListener('dragend', onDragLeave);
			document.querySelector('html').classList.remove('u_dropActive');
		};
	}, [files]); // keep latest files in scope for drop

	placeholder = placeholder || null;
	label = label || null;

	const inputClasses = [s.input];
	const labelClasses = [s.label];

	if (!Array.isArray(variantClasses)) variantClasses = [variantClasses.toString()];
	variantClasses.filter((c) => !!c).forEach((c) => {
		if (c.indexOf('label') !== -1) labelClasses.push(s[c]);
		else inputClasses.push(s[c]);
	});

	if (error) {
		labelClasses.push(s.label__error);
		inputClasses.push(s.input__error);
	}

	const commonInputProps = {
		className: inputClasses.join(' '),
		id,
		name: 'files',
		type: 'file',
		placeholder,
		onChange: inputChangeHandler,
		ref: fileInputRef,
	};

	const input = <input {...commonInputProps} {...props} />;

	const triggerUploadInput = () => {
		fileInputRef.current?.click();
	};

	const button = (
		<>
			<Button type="button" variantClasses={['button__upload']} clickEventHandler={triggerUploadInput} aria-label={label || 'Upload file'}>
				{ children || buttonLabel }
			</Button>
			{ buttonInfoLabel && <span className={s.upload_buttonInfoLabel}>{ buttonInfoLabel }</span> }
		</>
	);

	const clearAllUploadedFilesHandler = () => {
		syncToParentAndInput([]);
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const FileListUI = files.length === 0 ? null : (
		<div className={s.upload_list} aria-live="polite">
			<ul className={s.upload_list_items}>
				{
					files.map((f, idx) => {
						const removeUploadedFileHandler = () => {
							const next = files.filter((_, i) => i !== idx);
							syncToParentAndInput(next);
							// If empty, also clear the real input so user can re-select same file
							if (next.length === 0 && fileInputRef.current) {
								fileInputRef.current.value = '';
							}
						};

						return (
							<li key={`${f.name}-${idx}`} className={s.upload_list_item}> {/* eslint-disable-line */}
								<Button variantClasses={['button__file']} type="button" aria-label={`Remove ${f.name}`} clickEventHandler={removeUploadedFileHandler}>
									<span className={s.upload_list_item_name} title={f.name}>{ f.name }</span>
									<span>×</span>
								</Button>
							</li>
						);
					})
				}
			</ul>
			{
				files.length > 1 && (
					<Button type="button" variantClasses={['button__miniCTA']} clickEventHandler={clearAllUploadedFilesHandler}>
						Clear all
					</Button>
				)
			}
		</div>
	);

	const content = label ? (
		<label className={labelClasses.join(' ')} htmlFor={id}>
			<span className={s.label_text}>{ label }</span>
			<div className={s.upload_container}>
				{ input }
				{ button }
			</div>
		</label>
	) : (
		<div className={s.upload_container}>
			{ input }
			{ button }
		</div>
	);

	return (
		<div className={s.upload}>
			{ content }
			{ FileListUI }
		</div>
	);
};

export default Upload;
